"use client";

import React, { useState, useEffect, useCallback, useRef } from 'react';
import CommentItem from './CommentItem';
import CommentForm from './CommentForm';
import { getCommentsByNoteId, deleteComment, supabase, getProfileById } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import { Session } from '@supabase/supabase-js';
import { Loader2 } from '@/components/icons';
import { Skeleton } from '@/components/ui/skeleton';
import { RealtimeChannel } from '@supabase/supabase-js';
import { motion, AnimatePresence } from 'framer-motion';

interface Comment {
  id: string;
  created_at: string;
  content: string;
  user_id: string;
  parent_id?: string | null;
  profiles: {
    username: string;
    avatar_url: string | null;
  } | null;
}

interface NoteActivityFeedProps {
  noteId: string;
  session: Session | null;
  noteAuthorId: string;
  noteAuthorUsername: string;
  autoFocus?: boolean;
}

const NoteActivityFeed: React.FC<NoteActivityFeedProps> = ({ 
  noteId, 
  session, 
  noteAuthorId, 
  noteAuthorUsername,
  autoFocus = false 
}) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState<{ id: string; username: string } | null>(null);
  const { toast } = useToast();
  const activityContainerRef = useRef<HTMLDivElement>(null);

  const currentUserId = session?.user?.id;

  const fetchComments = useCallback(async () => {
    setLoading(true);
    try {
      const fetchedComments = await getCommentsByNoteId(noteId);
      // Sort by created_at in ASCENDING order (oldest first, newest at bottom)
      const sortedComments = (fetchedComments as Comment[]).sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
      setComments(sortedComments);
    } catch (error) {
      console.error("NoteActivityFeed ERROR: Error fetching comments:", error);
      toast({
        title: "Error al cargar comentarios",
        description: "No pudimos cargar los comentarios. Probá de nuevo.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [noteId, toast]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  // Realtime subscription for comments
  useEffect(() => {
    if (!noteId) {
      return;
    }


    const commentChannel: RealtimeChannel = supabase
      .channel(`note_comments_${noteId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'comments',
          filter: `note_id=eq.${noteId}`
        },
        async (payload) => {
          if (payload.eventType === 'INSERT' && payload.new) {
            const newComment = payload.new as Comment;
            const profile = await getProfileById(newComment.user_id);
            const commentWithProfile: Comment = {
              ...newComment,
              profiles: profile ? { username: profile.username, avatar_url: profile.avatar_url } : null,
            };
            setComments(prevComments => {
              const updatedComments = [...prevComments, commentWithProfile];
              // Keep sorted by created_at in ASCENDING order
              updatedComments.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
              return updatedComments;
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'comments',
          filter: `note_id=eq.${noteId}`
        },
        async (payload) => {
          if (payload.eventType === 'DELETE' && payload.old) {
            const deletedCommentId = (payload.old as Comment).id;
            setComments(prevComments => {
              const updatedComments = prevComments.filter(comment => comment.id !== deletedCommentId);
              return updatedComments;
            });
          }
        }
      )
      .subscribe((status) => {
      });

    return () => {
      supabase.removeChannel(commentChannel);
    };
  }, [noteId]);

  // Scroll to bottom on new comment
  useEffect(() => {
    if (activityContainerRef.current) {
      activityContainerRef.current.scrollTop = activityContainerRef.current.scrollHeight;
    }
  }, [comments]);

  const handleCommentAdded = () => {
    setReplyingTo(null); // Clear reply state when comment is added
  };

  const handleDeleteComment = async (commentId: string) => {
    // Optimistic update - remove from UI immediately
    setComments((prevComments) => prevComments.filter((c) => c.id !== commentId));

    try {
      await deleteComment(commentId);
      // Deletion will be handled by realtime subscription
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast({
        title: "Error al eliminar comentario",
        description: "No pudimos eliminar el comentario. Fijate si tenés permisos.",
        variant: "destructive",
      });
    }
  };

  const handleReply = (commentId: string, username: string) => {
    setReplyingTo({ id: commentId, username });
  };

  // Function to render comments recursively
  const renderComments = (parentId: string | null = null, level: number = 0) => {
    return comments
      .filter((c) => (parentId === null ? !c.parent_id : c.parent_id === parentId))
      .map((comment) => (
        <motion.div
          key={comment.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className={level > 0 ? "border-l-2 border-primary/30 pl-3" : ""}
        >
          <CommentItem
            comment={comment}
            currentUserId={currentUserId}
            noteAuthorId={noteAuthorId}
            onDeleteComment={handleDeleteComment}
            noteId={noteId}
            noteAuthorUsername={noteAuthorUsername}
            onCommentAdded={handleCommentAdded}
            onReply={handleReply}
            level={level}
          >
            {renderComments(comment.id, level + 1)}
          </CommentItem>
        </motion.div>
      ));
  };

  return (
    <div className="space-y-0 relative">
      <div ref={activityContainerRef} className="space-y-3 max-h-60 overflow-y-auto overflow-x-hidden pr-2 border rounded-t-md p-2 bg-background">
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : comments.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            Sé el primero en comentar.
          </p>
        ) : (
          <AnimatePresence initial={false}>
            {renderComments()}
          </AnimatePresence>
        )}
      </div>
      
      {/* Comment Form with reply state */}
      <div className="border border-t-0 rounded-b-md bg-background p-2 relative z-10">
        {replyingTo && (
          <div className="mb-2 text-xs text-muted-foreground flex items-center justify-between">
            <span>
              Estás respondiendo a <span className="text-primary font-medium">@{replyingTo.username}</span>
            </span>
            <button
              onClick={() => setReplyingTo(null)}
              className="text-xs text-muted-foreground hover:text-primary transition-colors flex-shrink-0"
            >
              ✕ Cancelar
            </button>
          </div>
        )}
        <CommentForm
          noteId={noteId}
          session={session}
          onCommentAdded={handleCommentAdded}
          noteAuthorId={noteAuthorId}
          noteAuthorUsername={noteAuthorUsername}
          autoFocus={autoFocus}
          parentId={replyingTo?.id}
          placeholder={replyingTo ? `Respondiendo a @${replyingTo.username}...` : "Escribí algo..."}
        />
      </div>
    </div>
  );
};

export default NoteActivityFeed;
